import React from 'react';
import {
  Typography,
  Paper,
  Box,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  MenuBook as GuideIcon,
  LocalGroceryStore as ShoppingIcon,
  Restaurant as DiningIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
} from '@mui/icons-material';

const Guide = () => {
  return (
    <Box>
      {/* Guide Header */}
      <Paper sx={{ p: 4, mb: 4, borderRadius: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ color: '#ff6b6b', fontWeight: 'bold', textAlign: 'center' }}>
          Průvodce bezlepkovou dietou
        </Typography>
        <Typography paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 3 }}>
          Komplexní průvodce pro osoby s celiakií a ty, kteří potřebují dodržovat bezlepkovou dietu. 
          Zde najdete vše potřebné od základních informací až po praktické tipy pro každodenní život.
        </Typography>
      </Paper>

      {/* Quick Navigation Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <GuideIcon sx={{ color: '#ff6b6b', mr: 1 }} />
                <Typography variant="h6">Základy</Typography>
              </Box>
              <Typography variant="body2">
                Co je lepek, kde se nachází a proč je důležité se mu vyhnout.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ShoppingIcon sx={{ color: '#ff6b6b', mr: 1 }} />
                <Typography variant="h6">Nakupování</Typography>
              </Box>
              <Typography variant="body2">
                Jak číst etikety a kde nakupovat bezlepkové potraviny.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <DiningIcon sx={{ color: '#ff6b6b', mr: 1 }} />
                <Typography variant="h6">Stravování</Typography>
              </Box>
              <Typography variant="body2">
                Tipy pro stravování doma i v restauracích.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <WarningIcon sx={{ color: '#ff6b6b', mr: 1 }} />
                <Typography variant="h6">Rizika</Typography>
              </Box>
              <Typography variant="body2">
                Na co si dát pozor a jak se vyhnout kontaminaci.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Content */}
      <Grid container spacing={4}>
        <Grid item xs={12} md={8}>
          {/* Basics Section */}
          <Paper sx={{ p: 4, mb: 4, borderRadius: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ color: '#ff6b6b', fontWeight: 'bold' }}>
              Základy bezlepkové diety
            </Typography>
            <Typography paragraph>
              Bezlepková dieta je nezbytná pro osoby s celiakií a může být prospěšná pro lidi 
              s citlivostí na lepek. Vyžaduje vyloučení všech potravin obsahujících lepek, 
              který se nachází především v pšenici, žitu, ječmeni a někdy v ovsu.
            </Typography>
            <List>
              <ListItem>
                <ListItemIcon>
                  <InfoIcon sx={{ color: '#ff6b6b' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="Co je lepek?" 
                  secondary="Protein nacházející se v některých obilovinách, který dává těstu jeho elasticitu."
                />
              </ListItem>
              <Divider variant="inset" component="li" />
              <ListItem>
                <ListItemIcon>
                  <InfoIcon sx={{ color: '#ff6b6b' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="Kde se nachází?" 
                  secondary="V běžném pečivu, těstovinách, pečených výrobcích a mnoha zpracovaných potravinách."
                />
              </ListItem>
              <Divider variant="inset" component="li" />
              <ListItem>
                <ListItemIcon>
                  <InfoIcon sx={{ color: '#ff6b6b' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="Proč se mu vyhnout?" 
                  secondary="U celiaků způsobuje poškození střevní sliznice a může vést k vážným zdravotním problémům."
                />
              </ListItem>
            </List>
          </Paper>

          {/* Shopping Tips */}
          <Paper sx={{ p: 4, mb: 4, borderRadius: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ color: '#ff6b6b', fontWeight: 'bold' }}>
              Tipy pro nakupování
            </Typography>
            <Typography paragraph>
              Správný výběr potravin je základem úspěšné bezlepkové diety. Je důležité umět 
              číst etikety a znát spolehlivé zdroje bezlepkových potravin.
            </Typography>
            <List>
              <ListItem>
                <ListItemIcon>
                  <ShoppingIcon sx={{ color: '#ff6b6b' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="Čtení etiket" 
                  secondary="Vždy kontrolujte složení a hledejte certifikační značky bezlepkovosti."
                />
              </ListItem>
              <Divider variant="inset" component="li" />
              <ListItem>
                <ListItemIcon>
                  <ShoppingIcon sx={{ color: '#ff6b6b' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="Kde nakupovat" 
                  secondary="Specializované prodejny, zdravé výživy a většina supermarketů má bezlepkové oddělení."
                />
              </ListItem>
            </List>
          </Paper>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          {/* Quick Tips */}
          <Paper sx={{ p: 3, mb: 4, borderRadius: 3, bgcolor: '#fff3e0' }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#e65100', fontWeight: 'bold' }}>
              Rychlé tipy
            </Typography>
            <List dense>
              <ListItem>
                <Typography variant="body2">• Používejte naši aplikaci pro skenování produktů</Typography>
              </ListItem>
              <ListItem>
                <Typography variant="body2">• Vytvořte si seznam ověřených produktů</Typography>
              </ListItem>
              <ListItem>
                <Typography variant="body2">• Vždy čtěte etikety, i u známých produktů</Typography>
              </ListItem>
              <ListItem>
                <Typography variant="body2">• Pozor na křížovou kontaminaci</Typography>
              </ListItem>
            </List>
          </Paper>

          {/* Important Notice */}
          <Paper sx={{ p: 3, borderRadius: 3, bgcolor: '#ffebee' }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#c62828', fontWeight: 'bold' }}>
              Důležité upozornění
            </Typography>
            <Typography variant="body2">
              Informace v tomto průvodci jsou pouze orientační. Vždy konzultujte svůj 
              zdravotní stav a dietu s lékařem nebo nutričním specialistou.
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Guide;
